import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { skipLoader } from '@rsm/shared/loader';
import { environment } from 'src/environments/environment';
import { Action } from './models/action';
import {
  MuralCommentPage,
  MuralPage,
  MuralPost,
  MuralComment,
  MuralReactionSummary,
} from './models/mural';
import {LicenseSnapshot} from "../../app/pages/company-details/company-financial/company-financial.component";

export interface ProfilePhotoRequest {
  id: string;
  userId?: string;
  userContextId?: string;
  contextId?: string;
  userLogin?: string;
  userName?: string;
  originalFilename: string;
  contentType: string;
  fileSize: number;
  status: string;
  imageUrl?: string | null;
  groupPhotoDetected?: boolean | null;
  detectedFaceCount?: number | null;
  faceLocation?: FaceBiometricFaceLocation | null;
  createdAt: string;
  statusUpdatedAt: string;
}

/** Ação da campanha de adesão Politics — o que o app mostra no card do convite. */
/** Cor disponível para o app, como o servidor a define. */
export interface AppColor {
  /** BLUE | YELLOW | RED | GREEN | PINK | BLACK. */
  name: string;
  label: string;
  hex: string;
  /** Cor do texto sobre a cor base. */
  contrast: string;
}

export interface CompanyProductType {
  contextId: string;
  /** Tipo EFETIVO: já com a emulação da empresa root aplicada pelo back. */
  productType: string;
  /** Tipo REAL do plano, sem emulação (`ROOT` para a RSM). */
  actualProductType?: string;
  /** Verdadeiro quando `productType` veio de emulação, não do plano. */
  rootEmulation?: boolean;
  planName: string;
  active: boolean;
  /** Cor base do app da empresa; ausente em resposta de versão anterior do back. */
  appColor?: string;
  appColorHex?: string;
  appColorContrast?: string;
}

export interface PoliticsOptInCampaign {
  id: string;
  titleAction?: string;
  descriptionAction?: string;
  /** Sentinela 'politics-optin-default' enquanto o cliente não subir a própria arte. */
  uriImageAction?: string;
}

export interface TaskTypePointRule {
  id: string;
  description?: string;
  defaultPoints: number;
  /**
   * Pontuação quando a campanha é de empresa RSM Politics. Vazio mantém o
   * comportamento atual (usa a pontuação comercial). No Politics o ponto não é
   * ganho do apoiador: cobre custo de máquina e remuneração da RSM.
   */
  politicsPoints?: number | null;
}

export interface FaceBiometricAnalysisRequest {
  userId: string;
  agentMissionId?: string | null;
  probeSourceId?: string | null;
  probeImageUrl: string;
  allowFallback?: boolean;
  groupPhotoMode?: boolean;
}

export interface FaceBiometricFaceLocation {
  left?: number | null;
  top?: number | null;
  right?: number | null;
  bottom?: number | null;
  width?: number | null;
  height?: number | null;
}

export interface FaceBiometricAnalysisResponse {
  analysisId: string;
  userId: string;
  agentMissionId?: string | null;
  correlationId?: string | null;
  provider?: string | null;
  providerRequestId?: string | null;
  status: string;
  decision: string;
  approved: boolean;
  fallbackUsed: boolean;
  similarityScore?: number | null;
  referenceId?: string | null;
  referenceSourceType?: string | null;
  referenceSourceId?: string | null;
  referenceImageUrl?: string | null;
  probeSourceId?: string | null;
  probeImageUrl?: string | null;
  message?: string | null;
  userPresent?: boolean | null;
  groupPhotoDetected?: boolean | null;
  ambiguityStatus?: string | null;
  referenceDetectedFaceCount?: number | null;
  probeDetectedFaceCount?: number | null;
  referenceFaceLocation?: FaceBiometricFaceLocation | null;
  probeFaceLocation?: FaceBiometricFaceLocation | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface KycSessionRequest {
  userId: string;
  agentMissionId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  identificationNumber?: string | null;
  language?: string | null;
  forceNewSession?: boolean | null;
}

export interface KycSessionResponse {
  verificationId: string;
  diditSessionId?: string | null;
  url?: string | null;
  status: string;
  userId: string;
}

export interface KycStatusResponse {
  userId: string;
  verificationId?: string | null;
  diditSessionId?: string | null;
  status: string;
  approved: boolean;
  updatedAt?: string | null;
}

export interface PagedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

export interface FaceBiometricManualReviewListItem {
  analysisId: string;
  userId: string;
  userLogin?: string | null;
  userName?: string | null;
  profilePhotoRequestId?: string | null;
  originalFilename?: string | null;
  provider?: string | null;
  similarityScore?: number | null;
  automaticStatus: string;
  automaticDecision: string;
  manualReviewStatus: string;
  manualReviewLockLogin?: string | null;
  manualReviewLockName?: string | null;
  manualReviewLockedAt?: string | null;
  profilePhotoStatus?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface FaceBiometricManualReviewAudit {
  id: string;
  analysisId: string;
  action: string;
  actionLabel?: string | null;
  manualReviewStatus: string;
  manualReviewStatusLabel?: string | null;
  decision?: string | null;
  decisionLabel?: string | null;
  operatorLogin: string;
  operatorName: string;
  reason?: string | null;
  reasonLabel?: string | null;
  createdAt?: string | null;
}

export interface FaceBiometricManualReviewDetail {
  analysisId: string;
  userId: string;
  userLogin?: string | null;
  userName?: string | null;
  agentMissionId?: string | null;
  profilePhotoRequestId?: string | null;
  originalFilename?: string | null;
  profilePhotoStatus?: string | null;
  profilePhotoStatusLabel?: string | null;
  correlationId?: string | null;
  provider?: string | null;
  providerRequestId?: string | null;
  automaticStatus: string;
  automaticStatusLabel?: string | null;
  automaticDecision: string;
  automaticDecisionLabel?: string | null;
  automaticApproved: boolean;
  fallbackUsed: boolean;
  similarityScore?: number | null;
  referenceId?: string | null;
  referenceSourceType?: string | null;
  referenceSourceId?: string | null;
  referenceImageUrl?: string | null;
  probeSourceId?: string | null;
  probeImageUrl?: string | null;
  automaticMessage?: string | null;
  automaticMessageLabel?: string | null;
  rawPayload?: string | null;
  manualReviewStatus: string;
  manualReviewStatusLabel?: string | null;
  manualReviewDecision?: string | null;
  manualReviewDecisionLabel?: string | null;
  manualReviewReason?: string | null;
  manualReviewReasonLabel?: string | null;
  manualReviewLockLogin?: string | null;
  manualReviewLockName?: string | null;
  manualReviewLockedAt?: string | null;
  manualReviewOperatorLogin?: string | null;
  manualReviewOperatorName?: string | null;
  manualReviewedAt?: string | null;
  lockedByCurrentOperator: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
  auditHistory: FaceBiometricManualReviewAudit[];
}

export interface FaceBiometricManualReviewDecisionRequest {
  decision: 'APPROVED' | 'REJECTED';
  reason: string;
}

export interface LinkedCompanyUsers {
  companyId: string;
  contextId: string;
  name: string;
  fantasyName: string;
  cnpj: string;
  /** Profundidade na hierarquia (1 = vinculada direta, 2 = vinculada da vinculada…). */
  level: number;
  /** Quantidade de usuários ativos (convite aceito) da empresa. */
  userCount: number;
  /** Quantidade de usuários com perfil completo ("estrela"). */
  starCount: number;
}

/** Preferência (por usuário/empresa) de carregar o card "Bases vinculadas" ao abrir a tela. */
export interface LinkedBasesPreference {
  enabled: boolean;
}

/** Indicadores do card "Base de Agentes" (Home) da empresa autenticada. */
export interface AgentDashboard {
  licensesAvailable: number;
  licensesInUse: number;
  licensesPending: number;
  licensesExpired: number;
  licensesTotal: number;
  /** Total de licenças (qualquer status) compradas sob o plano Politics (data fixa contratual). */
  licensesPolitics: number;
  /** Total de licenças (qualquer status) compradas avulsas (renováveis a cada 30 dias). */
  licensesAvulsa: number;
  agentsTotal: number;
  agentsStar: number;
  invitedLevelZero: number;
  invitedByAgents: number;
}

/**
 *
 */
@Injectable({
  providedIn: 'root',
})
export class MissionClientService {
  /**
   * @param http
   */
  constructor(private http: HttpClient) { }

  /**
   * Tipo de produto/plano ATIVO da empresa (POLITICS | DEDICATED | OTHER).
   * Usado para avisar sobre a quarentena de 9 meses ao excluir agente de rede Politics.
   */
  getCompanyProductType(contextId: string): Observable<CompanyProductType> {
    return this.http.get<CompanyProductType>(
      `${environment.mission.url}/plains/company/${contextId}/product-type`
    );
  }

  /** Paleta disponível para o app. Vem do servidor para não duplicar o hexadecimal. */
  getAppColors(): Observable<AppColor[]> {
    return this.http.get<AppColor[]>(`${environment.mission.url}/plains/company/app-colors`);
  }

  /** Troca a cor base do app dos agentes. Só dono da conta e administradores. */
  updateCompanyAppColor(contextId: string, color: string): Observable<CompanyProductType> {
    return this.http.put<CompanyProductType>(
      `${environment.mission.url}/plains/company/${contextId}/app-color`,
      { color }
    );
  }

  /**
   * Declara qual produto a empresa ROOT (RSM) está emulando; `null` limpa.
   *
   * <p>O plano ROOT é neutro — não herda o comportamento de nenhum produto. Para
   * operar e dar suporte dentro de um produto, a RSM escolhe explicitamente o
   * chapéu que está usando. Restrito a root no servidor.</p>
   */
  updateEmulatedProductType(
    contextId: string,
    productType: string | null
  ): Observable<CompanyProductType> {
    return this.http.put<CompanyProductType>(
      `${environment.mission.url}/plains/company/${contextId}/emulated-product-type`,
      { productType }
    );
  }

  /**
   * Empresas vinculadas (descendência) do patrocinador logado, com nível na
   * hierarquia e quantidade de usuários ativos — alimenta o card "Bases vinculadas"
   * no painel de usuários. `search`/`filterLocation` (opcionais) fazem a contagem
   * acompanhar o filtro inteligente do painel; a árvore de empresas não muda.
   */
  getLinkedCompaniesUsersSummary(
    search?: string,
    filterLocation?: string
  ): Observable<LinkedCompanyUsers[]> {
    let params = new HttpParams();
    if (search && search.trim()) {
      params = params.set('search', search.trim());
    }
    if (filterLocation && filterLocation.trim()) {
      params = params.set('filterLocation', filterLocation.trim());
    }
    return this.http.get<LinkedCompanyUsers[]>(
      `${environment.mission.url}/linked-companies/users-summary`,
      { params }
    );
  }

  /**
   * Preferência do usuário para carregar o card "Bases vinculadas" ao abrir a tela de
   * usuários. Como a montagem do card é cara, o carregamento é opt-in (padrão desligado).
   */
  getLinkedBasesPreference(): Observable<LinkedBasesPreference> {
    return this.http.get<LinkedBasesPreference>(
      `${environment.mission.url}/linked-companies/users-summary/preference`
    );
  }

  updateLinkedBasesPreference(enabled: boolean): Observable<LinkedBasesPreference> {
    return this.http.put<LinkedBasesPreference>(
      `${environment.mission.url}/linked-companies/users-summary/preference`,
      { enabled }
    );
  }

  /**
   * Indicadores do card "Base de Agentes" (Home): licenças (disponíveis/em
   * uso/total) e base de agentes (total, estrela, convidados por origem).
   */
  getAgentDashboard(): Observable<AgentDashboard> {
    return this.http.get<AgentDashboard>(`${environment.mission.url}/agents/dashboard`);
  }

  /**
   *
   * @param data
   * @returns
   */
  public salvarCampanha(data: any): Observable<any> {
    return this.http.post<any>(`${environment.mission.url}/campaign`, {
      ...data,
    });
  }

  /**
   * @param data
   * @returns
   */
  public editarCampanha(data: any): Observable<any> {
    return this.http.put<any>(`${environment.mission.url}/campaign`, data);
  }

  /**
   *
   * @param data
   * @returns
   */
  public salvarAcao(data: any): Observable<any> {
    return this.http.post<any>(`${environment.mission.url}/action`, {
      ...data,
    });
  }

  /**
   * @param data
   * @returns
   */
  public editarAcao(data: any): Observable<any> {
    return this.http.put<any>(`${environment.mission.url}/action`, data);
  }

  /**
   *
   * @param data
   * @returns
   */
  public salvarTask(data: any): Observable<any> {
    return this.http.post<any>(`${environment.mission.url}/task`, { ...data });
  }

  /**
   * @param data
   * @returns
   */
  public editarTask(data: any): Observable<any> {
    return this.http.put<any>(`${environment.mission.url}/task`, data);
  }

  /**
   * Remove a Tarefa.
   *
   * @param taskId
   * @returns
   */
  public deleteTask(taskId: string) {
    return this.http.delete<any>(
      `${environment.mission.url}/task/${taskId}`
    );
  }

  /**
   *
   * @param data
   * @returns
   */
  public salvarSegmentation(data: any): Observable<any> {
    return this.http.post<any>(`${environment.mission.url}/segmentation`, {
      ...data,
    });
  }

  /**
   * @param data
   * @returns
   */
  public editarSegmentation(data: any): Observable<any> {
    return this.http.put<any>(`${environment.mission.url}/segmentation`, data);
  }

  /**
   * @returns
   */
  public getCampaigns(
    filter: any = { idStatus: '', page: 0, size: 4 }
  ): Observable<any> {
    return this.http.get<any>(`${environment.mission.url}/campaign/user`, {
      params: {
        ...filter,
      },
    });
  }

  /**
   * @returns
   */
  public getTypeCampaign(): Observable<
    {
      qtd: number;
      idStatusCampaign: string;
      descriptionStatusCampaign: string;
    }[]
  > {
    return this.http.get<any>(
      `${environment.mission.url}/campaign/status/user`
    );
  }

  /**
   * @returns
   */
  public getMissionId(id: any): Observable<any> {
    return this.http.get<any>(`${environment.mission.url}/campaign/` + id);
  }

  /**
   * @returns
   */
  /**
   * Métricas agregadas do dashboard da Home numa única chamada (calculadas no
   * banco). Substitui o fan-out que fazia 2 requisições por campanha.
   */
  public getCampaignDashboardMetrics(): Observable<{
    actionsCreated: number;
    actionsExecuted: number;
    pointsAllocated: number;
    pointsDistributed: number;
    reachTotal: number;
    reachUniqueEstimated: number;
  }> {
    return this.http.get<any>(
      `${environment.mission.url}/campaign/dashboard-metrics`
    );
  }

  public getCampaignActions(id: any): Observable<{
    titleCampaign: string;
    descriptionCampaign: string;
    actionList: Action[];
  }> {
    return this.http.get<any>(
      `${environment.mission.url}/actions/campaign/` + id
    );
  }

  /**
   * @returns
   */
  public getSegmentationsMissionId(id: any): Observable<any> {
    return this.http.get<any>(`${environment.mission.url}/segmentation/` + id);
  }

  public getAction(actionId: string) {
    return this.http.get<Action>(
      `${environment.mission.url}/action/${actionId}`
    );
  }

  public getSegmentationByAction(actionId: string): Observable<any> {
    return this.http.get<any>(`${environment.mission.url}/segmentation/action/${actionId}`);
  }

  /**
   * Renova URLs de imagem/vídeo/arquivo da tarefa cujo presign expirou (bucket privado,
   * TTL curto). Aceita a URL estática OU já presignada (o backend descarta a query string
   * e re-assina pela key) — devolve um mapa url informada → URL presignada fresca.
   */
  public presignImageUrls(urls: string[]): Observable<{ [url: string]: string }> {
    return this.http.post<{ [url: string]: string }>(
      `${environment.mission.url}/action/presign-image-urls`,
      urls
    );
  }

  /**
   * @returns
   */
  public getActionTypes(): Observable<any> {
    return this.http.get<any>(`${environment.mission.url}/action/type`);
  }

  public deleteAction(actionId: string) {
    return this.http.delete<any>(
      `${environment.mission.url}/action/${actionId}`
    );
  }

  /**
   *
   * @returns
   */
  public getOperationsChannels(): Observable<any> {
    return this.http.get<any>(`${environment.mission.url}/operating/channel`);
  }

  /**
   *
   * @returns
   */
  /**
   * Campanha de adesão "Aceite Voluntário Politics" da empresa logada. Só existe
   * para empresa do produto RSM Politics; para as demais o backend responde 204
   * com corpo vazio — ausência, não erro.
   */
  public getPoliticsOptInCampaign(): Observable<PoliticsOptInCampaign | null> {
    return this.http.get<PoliticsOptInCampaign | null>(`${environment.mission.url}/politics/optin-campaign`);
  }

  /** Substitui a arte do card do convite pela do cliente. */
  public updatePoliticsOptInArt(filename: string, base64Data: string): Observable<PoliticsOptInCampaign> {
    return this.http.put<PoliticsOptInCampaign>(
      `${environment.mission.url}/politics/optin-campaign/art`,
      { filename, base64Data }
    );
  }

  /** Volta para a arte padrão do convite. */
  public resetPoliticsOptInArt(): Observable<PoliticsOptInCampaign> {
    return this.http.delete<PoliticsOptInCampaign>(`${environment.mission.url}/politics/optin-campaign/art`);
  }

  public getTaskTypes(): Observable<any> {
    return this.http.get<any>(`${environment.mission.url}/task/type`);
  }

  public updateTaskTypePoints(body: TaskTypePointRule[]): Observable<TaskTypePointRule[]> {
    return this.http.put<TaskTypePointRule[]>(`${environment.mission.url}/task/type/points`, body);
  }

  startCampaign(campaignId: string) {
    return this.http.put(
      `${environment.mission.url}/${campaignId}/start`,
      null
    );
  }

  removeCampaign(campaignId: string) {
    return this.http.delete(
      `${environment.mission.url}/campaign/${campaignId}`
    );
  }

  approveCampaign(campaignId: string) {
    return this.http.put(
      `${environment.mission.url}/${campaignId}/approve`,
      null
    );
  }

  uploadProfilePhoto(file: File): Observable<ProfilePhotoRequest> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<ProfilePhotoRequest>(
      `${environment.mission.url}/profile-photos`,
      formData
    );
  }

  getProfilePhotoRequest(requestId: string): Observable<ProfilePhotoRequest> {
    return this.http.get<ProfilePhotoRequest>(
      `${environment.mission.url}/profile-photos/${requestId}`
    );
  }

  /**
   * Avatar do usuário logado. Carrega sozinho junto com o menu, então roda
   * silencioso: é enfeite de tela, não pode bloquear a navegação.
   */
  getCurrentApprovedProfilePhoto(): Observable<ProfilePhotoRequest | null> {
    return this.http.get<ProfilePhotoRequest | null>(
      `${environment.mission.url}/profile-photos/me/approved`,
      { context: skipLoader() }
    );
  }

  /**
   * Foto de perfil aprovada de um agente específico (painel → detalhe do agente).
   * Mesma regra do app: foto enviada aprovada ou, na ausência, a selfie do KYC.
   */
  getApprovedProfilePhotoByUserId(userId: string): Observable<ProfilePhotoRequest | null> {
    return this.http.get<ProfilePhotoRequest | null>(
      `${environment.mission.url}/profile-photos/user/${userId}/approved`
    );
  }

  // ------------------------------------------------------------------ //
  // MURAL                                                                //
  // ------------------------------------------------------------------ //

  getMuralFeed(page: number, size: number): Observable<MuralPage> {
    return this.http.get<MuralPage>(`${environment.mission.url}/mural`, {
      params: { page: page.toString(), size: size.toString() },
    });
  }

  createMuralPost(content: string, targetAudience: string, files: File[], targetContextIds: string[] = []): Observable<MuralPost> {
    const formData = new FormData();
    const payload: { content: string; targetAudience: string; targetContextIds?: string[] } = { content, targetAudience };
    if (targetContextIds && targetContextIds.length > 0) {
      payload.targetContextIds = targetContextIds;
    }
    const data = new Blob([JSON.stringify(payload)], {
      type: 'application/json',
    });
    formData.append('data', data);
    files.forEach((file) => formData.append('files', file));
    return this.http.post<MuralPost>(`${environment.mission.url}/mural`, formData);
  }

  deleteMuralPost(postId: string): Observable<void> {
    return this.http.delete<void>(`${environment.mission.url}/mural/${postId}`);
  }

  toggleMuralReaction(postId: string, reactionType: string): Observable<MuralReactionSummary> {
    return this.http.post<MuralReactionSummary>(
      `${environment.mission.url}/mural/${postId}/reactions`,
      { reactionType },
    );
  }

  getMuralComments(postId: string, page: number, size: number): Observable<MuralCommentPage> {
    return this.http.get<MuralCommentPage>(
      `${environment.mission.url}/mural/${postId}/comments`,
      { params: { page: page.toString(), size: size.toString() } },
    );
  }

  addMuralComment(postId: string, content: string): Observable<MuralComment> {
    return this.http.post<MuralComment>(
      `${environment.mission.url}/mural/${postId}/comments`,
      { content },
    );
  }

  deleteMuralComment(commentId: string): Observable<void> {
    return this.http.delete<void>(`${environment.mission.url}/mural/comments/${commentId}`);
  }

  // ------------------------------------------------------------------ //
  // BIOMETRIC / KYC                                                      //
  // ------------------------------------------------------------------ //

  analyzeFaceBiometric(
    payload: FaceBiometricAnalysisRequest,
    correlationId?: string
  ): Observable<FaceBiometricAnalysisResponse> {
    const headers = correlationId
      ? new HttpHeaders({ 'X-Correlation-Id': correlationId })
      : undefined;

    return this.http.post<FaceBiometricAnalysisResponse>(
      `${environment.mission.url}/face-biometric/analyses`,
      payload,
      { headers }
    );
  }

  getFaceBiometricAnalysis(
    analysisId: string
  ): Observable<FaceBiometricAnalysisResponse> {
    return this.http.get<FaceBiometricAnalysisResponse>(
      `${environment.mission.url}/face-biometric/analyses/${analysisId}`
    );
  }

  createKycSession(payload: KycSessionRequest): Observable<KycSessionResponse> {
    return this.http.post<KycSessionResponse>(
      `${environment.mission.url}/kyc/session`,
      payload
    );
  }

  /**
   * @param options `silent: true` no polling de status — a tela já mostra o
   * próprio indicador e não deve ser bloqueada pelo overlay a cada 10s.
   */
  getKycStatus(userId: string, options?: { silent?: boolean }): Observable<KycStatusResponse> {
    return this.http.get<KycStatusResponse>(
      `${environment.mission.url}/kyc/status/${userId}`,
      options?.silent ? { context: skipLoader() } : {}
    );
  }

  listBiometricManualReviews(filter: {
    status?: string;
    provider?: string;
    user?: string;
    dateFrom?: string;
    dateTo?: string;
    page: number;
    size: number;
  }): Observable<PagedResponse<FaceBiometricManualReviewListItem>> {
    let params = new HttpParams()
      .set('page', String(filter.page))
      .set('size', String(filter.size));

    if (filter.status) {
      params = params.set('status', filter.status);
    }
    if (filter.provider) {
      params = params.set('provider', filter.provider);
    }
    if (filter.user) {
      params = params.set('user', filter.user);
    }
    if (filter.dateFrom) {
      params = params.set('dateFrom', filter.dateFrom);
    }
    if (filter.dateTo) {
      params = params.set('dateTo', filter.dateTo);
    }

    return this.http.get<PagedResponse<FaceBiometricManualReviewListItem>>(
      `${environment.mission.url}/face-biometric/manual-reviews`,
      { params }
    );
  }

  getBiometricManualReview(analysisId: string): Observable<FaceBiometricManualReviewDetail> {
    return this.http.get<FaceBiometricManualReviewDetail>(
      `${environment.mission.url}/face-biometric/manual-reviews/${analysisId}`
    );
  }

  claimBiometricManualReview(analysisId: string): Observable<FaceBiometricManualReviewDetail> {
    return this.http.post<FaceBiometricManualReviewDetail>(
      `${environment.mission.url}/face-biometric/manual-reviews/${analysisId}/claim`,
      {}
    );
  }

  decideBiometricManualReview(
    analysisId: string,
    payload: FaceBiometricManualReviewDecisionRequest
  ): Observable<FaceBiometricManualReviewDetail> {
    return this.http.post<FaceBiometricManualReviewDetail>(
      `${environment.mission.url}/face-biometric/manual-reviews/${analysisId}/decision`,
      payload
    );
  }

  getLicensesSummary(): Observable<LicenseSnapshot> {
    const url = `${environment.mission.url}/products/dashboard-company-financial`;
    return this.http.get<LicenseSnapshot>(url);
  }
}
